export type TicketAttachmentMetadata = {
  file_name: string
  path: string
  file_url?: string
}

/** Triage fields stored in extracted_fields and sent to ALICE pre-fill */
export type TicketTriageFields = {
  priority?: number
  storeId?: string
  storeCode?: string
  storeLabel?: string
  siteAddress?: string
  errorMessage?: string
  preferredScheduleUntil?: string
  contactAvailabilityText?: string
  workImpact?: string
  othersAffected?: string
  userScope?: string
  appScope?: string
  accessType?: string
  workPermitNeeded?: string
  permitAvailable?: string
  permitDuration?: string
  partsApproved?: string
  closeOnFix?: string
  timeSensitive?: string
}

export type StoreSearchResult = {
  id: number
  branch_code: string
  branch_name: string
  branch_address?: string | null
  location_address?: string | null
  normalized_address?: string | null
}

export type TicketRequest = {
  id?: number
  request_number?: string
  client_id?: number
  client_code?: string
  client_name?: string
  service_type_id?: number
  service_parent_id?: number | null
  service_type?: string
  service_parent?: string | null
  category_id?: number | null
  category?: string
  category_ids?: number[]
  description?: string
  email_subject?: string | null
  requested_quantity?: number
  preferred_schedule_at?: string | null
  preferred_schedule_until?: string | null
  preferred_contact_at?: string | null
  priority?: number
  attachment_metadata?: TicketAttachmentMetadata[]
  contact_number?: string
  contact_name?: string
  contact_email?: string
  request_source?: string
  request_channel?: string
  raw_message?: string | null
  raw_payload?: Record<string, unknown> | null
  extracted_fields?: Record<string, unknown> | null
  ticket_status?: number
  ticket_status_label?: string
  ticket_id?: number | null
  parent_ticket_id?: number | null
  parent_ticket_number?: string | null
  parent_ticket_status?: string | null
  acknowledged_at?: string | null
  submitted_at?: string | null
  created_at?: string
  updated_at?: string
  last_updated_at_human?: string | null
  external_reference_id?: string | null
}

export type TicketRequestSourceEmail = {
  subject: string | null
  body: string
  received_email_id: number
}

export type TicketFormOption = {
  label: string
  value: string
  parent_id?: string
}

export type TicketFormOptions = {
  serviceParents: TicketFormOption[]
  serviceChildren: TicketFormOption[]
  categories: TicketFormOption[]
}

export type WebTicketRequestBody = {
  service_type_id: number
  category_ids: number[]
  description: string
  requested_quantity: number
  preferred_schedule_at: string | null
  preferred_schedule_until: string | null
  preferred_contact_at: string | null
  attachment_metadata: TicketAttachmentMetadata[]
  contact_number: string
  contact_name: string
  contact_email: string
  request_source: string
  request_channel: string
  submit_request: boolean
  priority?: number
  store_id?: number | null
  store_code?: string | null
  site_address?: string | null
  error_message?: string | null
  work_impact?: string | null
  others_affected?: string | null
  user_scope?: string | null
  app_scope?: string | null
  access_type?: string | null
  work_permit_needed?: string | null
  permit_available?: string | null
  permit_duration?: string | null
  parts_approved?: string | null
  close_on_fix?: string | null
  time_sensitive?: string | null
  contact_availability_text?: string | null
  region_id?: number | null
  province_id?: number | null
  town_id?: number | null
  needs_human_review?: boolean
}

/** Maps TicketRequest from GET show to CreateTicketModal initialData */
export type TicketInitialData = Pick<
  TicketRequest,
  | 'id'
  | 'service_type_id'
  | 'service_parent_id'
  | 'category_id'
  | 'category_ids'
  | 'description'
  | 'requested_quantity'
  | 'preferred_schedule_at'
  | 'preferred_schedule_until'
  | 'preferred_contact_at'
  | 'priority'
  | 'contact_number'
  | 'contact_name'
  | 'contact_email'
  | 'attachment_metadata'
  | 'extracted_fields'
>

function strField(extracted: Record<string, unknown> | null | undefined, key: string): string {
  const v = extracted?.[key]
  if (v == null) return ''
  return String(v).trim()
}

export function triageFromTicketRequest(req: TicketRequest): TicketTriageFields {
  const ex = req.extracted_fields ?? {}
  const storeCode = strField(ex, 'store_code')
  const location = strField(ex, 'location') || strField(ex, 'site_address')
  return {
    priority: req.priority ?? 3,
    storeId: strField(ex, 'store_id'),
    storeCode,
    storeLabel: storeCode ? `${storeCode}${location ? ` — ${location}` : ''}` : '',
    siteAddress: location,
    errorMessage: strField(ex, 'error_message'),
    preferredScheduleUntil:
      (req.preferred_schedule_until ?? strField(ex, 'preferred_schedule_until')) || undefined,
    contactAvailabilityText:
      strField(ex, 'contact_availability_text') || strField(ex, 'contact_availability'),
    workImpact: strField(ex, 'work_impact'),
    othersAffected: strField(ex, 'others_affected'),
    userScope: strField(ex, 'user_scope'),
    appScope: strField(ex, 'app_scope'),
    accessType: strField(ex, 'access_type'),
    workPermitNeeded: strField(ex, 'work_permit_needed'),
    permitAvailable: strField(ex, 'permit_available'),
    permitDuration: strField(ex, 'permit_duration'),
    partsApproved: strField(ex, 'parts_approved'),
    closeOnFix: strField(ex, 'close_on_fix'),
    timeSensitive: strField(ex, 'time_sensitive'),
  }
}

export function ticketRequestToInitialData(req: TicketRequest): TicketInitialData {
  return {
    id: req.id,
    service_type_id: req.service_type_id,
    service_parent_id: req.service_parent_id ?? undefined,
    category_id: req.category_id ?? undefined,
    category_ids: req.category_ids ?? (req.category_id != null ? [req.category_id] : []),
    description: req.description,
    requested_quantity: req.requested_quantity,
    preferred_schedule_at: req.preferred_schedule_at,
    preferred_schedule_until: req.preferred_schedule_until,
    preferred_contact_at: req.preferred_contact_at,
    priority: req.priority,
    contact_number: req.contact_number,
    contact_name: req.contact_name,
    contact_email: req.contact_email,
    attachment_metadata: req.attachment_metadata,
    extracted_fields: req.extracted_fields,
  }
}

export function ticketRequestToRow(req: TicketRequest): Record<string, unknown> {
  return { ...req }
}

export function isOnsiteServiceParent(
  serviceParents: TicketFormOption[],
  serviceParentId: string
): boolean {
  if (!serviceParentId) return false
  const parent = serviceParents.find((p) => p.value === serviceParentId)
  if (!parent) return false
  return /onsite|on-site|on site|field/i.test(parent.label)
}
